import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { submitCustomAgentLead } from "@/lib/submitLeadInquiry";
import ChoiceMagneticButton from "../shared/ChoiceMagneticButton.jsx";
import { CheckIcon } from "../shared/icons.jsx";

const INDUSTRY_OPTIONS = [
  "HVAC",
  "Legal",
  "Dental",
  "Home Services",
  "Healthcare",
  "Insurance",
  "Other",
];

const MONTHLY_INBOUND_OPTIONS = [
  { value: "<50", label: "Fewer than 50" },
  { value: "50-200", label: "50 – 200" },
  { value: "200-500", label: "200 – 500" },
  { value: "500+", label: "500+" },
];

const PRIMARY_CHANNEL_OPTIONS = [
  { value: "", label: "Select (optional)" },
  { value: "phone", label: "Phone" },
  { value: "web_form", label: "Web form" },
  { value: "sms", label: "SMS" },
  { value: "mixed", label: "Mixed" },
];

const INITIAL_FORM = {
  name: "",
  email: "",
  industry: "",
  industryOther: "",
  monthlyInbound: "",
  primaryChannel: "",
};

const formInputClass =
  "w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 transition-colors";

const CustomAgentModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [status, setStatus] = useState("form");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && status !== "submitting") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose, status]);

  useEffect(() => {
    if (!isOpen) {
      setFormData(INITIAL_FORM);
      setStatus("form");
      setErrorMessage("");
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    if (status === "submitting") return;
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMessage("");

    const industry =
      formData.industry === "Other"
        ? formData.industryOther.trim()
        : formData.industry;

    try {
      await submitCustomAgentLead({
        name: formData.name.trim(),
        email: formData.email.trim(),
        industry,
        monthly_inbound: formData.monthlyInbound,
        primary_channel: formData.primaryChannel,
      });
      setStatus("success");
    } catch {
      setStatus("error");
      setErrorMessage("Something went wrong. Please try again or contact us directly.");
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="custom-agent-modal-overlay"
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleClose} aria-hidden="true" />

      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby="custom-agent-modal-title"
        className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto bento-card border border-purple-500/25 bg-[#0a0f1a] p-6 sm:p-8 shadow-2xl shadow-purple-500/10"
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        data-testid="custom-agent-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors p-1"
          aria-label="Close dialog"
          disabled={status === "submitting"}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {status === "success" ? (
          <div className="text-center py-6" data-testid="custom-agent-success">
            <div className="w-14 h-14 rounded-full bg-purple-500/10 border border-purple-500/30 flex items-center justify-center mx-auto mb-5 text-purple-400">
              <CheckIcon />
            </div>
            <h2 id="custom-agent-modal-title" className="text-2xl font-bold mb-3 text-white">
              Request Received
            </h2>
            <p className="text-gray-400 font-body leading-relaxed mb-6">
              Thank you — Step 1 is underway. Expect a reply within 24 hours.
            </p>
            <ChoiceMagneticButton
              className="w-full px-6 py-3 rounded-xl font-semibold"
              onClick={handleClose}
            >
              Close
            </ChoiceMagneticButton>
          </div>
        ) : (
          <>
            <h2 id="custom-agent-modal-title" className="text-2xl sm:text-3xl font-bold mb-2 text-white pr-8">
              Build Your Digital Employee
            </h2>
            <p className="text-sm text-gray-400 font-body mb-5 leading-relaxed">
              Same playbook: 9-second reply · qualify · log · book.
            </p>

            <div
              className="border border-purple-500/15 rounded-xl p-4 mb-5"
              data-testid="custom-agent-after-submit-steps"
            >
              <p className="text-xs font-mono text-purple-400/80 uppercase tracking-wide mb-3">
                What happens after you submit
              </p>
              <ol className="space-y-3 text-sm text-gray-400 font-body">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-500/15 border border-purple-500/30 text-purple-400 text-xs font-mono flex items-center justify-center">1</span>
                  <span>
                    <span className="text-white font-semibold">We review your industry &amp; volume</span>
                    {" "}— reply within 24 hours
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-500/15 border border-purple-500/30 text-purple-400 text-xs font-mono flex items-center justify-center">2</span>
                  <span>
                    <span className="text-white font-semibold">We scope your qualification protocol</span>
                    {" "}— trained on your inbound flow; custom CRM integrations quoted at kickoff
                  </span>
                </li>
              </ol>
            </div>

            {status === "error" && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl mb-5 text-sm">
                {errorMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="custom-agent-form">
              <div>
                <label htmlFor="custom-agent-name" className="block text-sm font-medium text-gray-400 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="custom-agent-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={formInputClass}
                  placeholder="Jane Smith"
                  disabled={status === "submitting"}
                />
              </div>

              <div>
                <label htmlFor="custom-agent-email" className="block text-sm font-medium text-gray-400 mb-2">
                  Work email
                </label>
                <input
                  type="email"
                  id="custom-agent-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={formInputClass}
                  placeholder="jane@company.com"
                  disabled={status === "submitting"}
                />
              </div>

              <div>
                <label htmlFor="custom-agent-industry" className="block text-sm font-medium text-gray-400 mb-2">
                  Industry
                </label>
                <select
                  id="custom-agent-industry"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  required
                  className={formInputClass}
                  disabled={status === "submitting"}
                >
                  <option value="" disabled>
                    Select your industry
                  </option>
                  {INDUSTRY_OPTIONS.map((option) => (
                    <option key={option} value={option} className="bg-[#0a0f1a]">
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              {formData.industry === "Other" && (
                <div>
                  <label htmlFor="custom-agent-industry-other" className="block text-sm font-medium text-gray-400 mb-2">
                    Specify industry
                  </label>
                  <input
                    type="text"
                    id="custom-agent-industry-other"
                    name="industryOther"
                    value={formData.industryOther}
                    onChange={handleChange}
                    required
                    className={formInputClass}
                    placeholder="Your industry"
                    disabled={status === "submitting"}
                  />
                </div>
              )}

              <div>
                <label htmlFor="custom-agent-monthly" className="block text-sm font-medium text-gray-400 mb-2">
                  Monthly inbound leads
                </label>
                <select
                  id="custom-agent-monthly"
                  name="monthlyInbound"
                  value={formData.monthlyInbound}
                  onChange={handleChange}
                  required
                  className={formInputClass}
                  disabled={status === "submitting"}
                >
                  <option value="" disabled>
                    Select volume
                  </option>
                  {MONTHLY_INBOUND_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value} className="bg-[#0a0f1a]">
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="custom-agent-channel" className="block text-sm font-medium text-gray-400 mb-2">
                  Primary inbound channel
                </label>
                <select
                  id="custom-agent-channel"
                  name="primaryChannel"
                  value={formData.primaryChannel}
                  onChange={handleChange}
                  className={formInputClass}
                  disabled={status === "submitting"}
                >
                  {PRIMARY_CHANNEL_OPTIONS.map((option) => (
                    <option key={option.value || "none"} value={option.value} className="bg-[#0a0f1a]">
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <ChoiceMagneticButton
                type="submit"
                className="w-full bg-gradient-to-r from-purple-600 via-purple-500 to-indigo-500 px-6 py-3.5 rounded-xl font-semibold mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={status === "submitting"}
                data-testid="custom-agent-submit"
              >
                {status === "submitting" ? "Submitting…" : "Request a Custom Digital Employee"}
              </ChoiceMagneticButton>
            </form>

            <p className="text-xs text-gray-500 font-body text-center mt-4">
              Prefer email?{" "}
              <Link to="/contact" className="text-purple-400 hover:text-purple-300 transition-colors">
                Contact us
              </Link>
            </p>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

export default CustomAgentModal;
